import { Button, Image } from '@nextui-org/react'
import { __ } from '@wordpress/i18n'
import { useCallback, useEffect, useState } from 'react'

import { useCheckout } from '../libs/useCheckout'

export interface OmisePromptpayScanProps {
  orderId: number
}

export function OmisePromptpayScan({
  orderId,
}: OmisePromptpayScanProps): JSX.Element {
  const { data, error, isLoading } = useCheckout().getOmisePromptpay(orderId)

  const downloadQR = useCallback(() => {
    const currentDate = new Date().toISOString().replace(/:/g, '-')
    const fileName = `${orderId}-qr-code-${currentDate}.png`
    const link = document.createElement('a')
    link.href = data?.image || '#'
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [data?.image, orderId])

  if (isLoading) {
    return <p>{__('Loading QR Payment...', 'asavagroup')}</p>
  }

  if (error) {
    return (
      <p>
        Error:{' '}
        {error?.message || __('Failed to load QR Payment code', 'asavagroup')}
      </p>
    )
  }

  if (data?.status === 'successful') {
    return <></>
  }

  return (
    <div>
      <p className="mb-4 text-center text-lg font-medium uppercase">
        {__('Scan the QR code to pay', 'asavagroup')}
      </p>
      <div className="mb-6 flex justify-center">
        <div>
          <Image alt="QR Code" src={data?.image} width={300} />
          <Button
            color="primary"
            fullWidth
            onPress={() => downloadQR()}
            type="button"
            variant="solid"
          >
            {__('Download QR', 'asavagroup')}
          </Button>
          <div className="mt-4 text-center">
            {__('Payment expires in: ', 'asavagroup')}{' '}
            {data?.expiresAt && (
              <CountdownTimer expiresAt={new Date(data?.expiresAt)} />
            )}
          </div>
        </div>
      </div>

      <div className="border p-6 text-left">
        <p className="mb-2 font-medium uppercase">
          {__('To make payment:', 'asavagroup')}
        </p>
        <ol className="ml-6 list-item list-decimal text-sm">
          <li>
            Download the QR code or open your preferred bank app to scan it
          </li>
          <li>Check that the payment details are correct</li>
          <li>
            Import the QR code image into your bank app or scan the QR code with
            your bank app to pay
          </li>
          <li>Share the payment slip from your bank app to the seller</li>
        </ol>
      </div>
    </div>
  )
}

function CountdownTimer({ expiresAt }: { expiresAt: Date }) {
  const calculateTimeLeft = useCallback(() => {
    const currentDate = new Date().toLocaleString('en-US', {
      timeZone: 'Asia/Bangkok',
    })
    return expiresAt.getTime() - new Date(currentDate).getTime()
  }, [expiresAt])

  const [timeLeft, setTimeLeft] = useState<number>(calculateTimeLeft())

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(calculateTimeLeft())
    }, 1000)

    return () => clearInterval(interval)
  }, [calculateTimeLeft, expiresAt])

  const formatTimeLeft = (milliseconds: number) => {
    const totalSeconds = Math.floor(milliseconds / 1000)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }

  return <span>{formatTimeLeft(timeLeft)}</span>
}
export default OmisePromptpayScan
